import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { ClassName } from 'types/ClassName';
import IDatePickerValue from 'types/DatePickerValue';
import ITimeRange from 'types/TimeRange';
import { getAdjustedEndDateChange, getAdjustedStartDateChange } from 'utils/dateUtils';
import DateRange, { IDateRangeProps } from '../DateRange/DateRange';

export interface IAdjustingDateRangeProps
  extends Omit<IDateRangeProps, 'startDate' | 'endDate' | 'onStartChange' | 'onEndChange'> {
  onChange: (dateRange: ITimeRange<DateTime | null>) => void;
  value: ITimeRange<DateTime | null>;
  className?: ClassName;
  /**
   * Opt-in callback fired when a boundary's text is non-empty but cannot be parsed into a
   * valid date. When provided, unparseable input is reported here instead of being committed
   * as a `null` value — committing null re-renders the picker with `value={null}`, which makes
   * the underlying DatePicker wipe the user's text. Omit this prop to preserve the original
   * behavior of treating invalid input the same as a cleared field.
   */
  onInvalidInput?: (boundary: 'start' | 'end', isInvalid: boolean) => void;
  /**
   * Opt-in: when `true`, a boundary cannot be left empty. Clearing a field that has a committed
   * value restores that value instead of committing `null`. This both enforces the "required"
   * rule and avoids the broken null state — the underlying DatePicker can't be typed into once
   * its value is `null` (it wipes its own text on every keystroke). Omit to allow clearing.
   */
  revertEmptyInput?: boolean;
}

function AdjustingDateRange({
  value,
  onChange,
  onInvalidInput,
  revertEmptyInput,
  className,
  ...rest
}: IAdjustingDateRangeProps) {
  const startTime = value?.startTime ?? null;
  const endTime = value?.endTime ?? null;

  // Bumping this remounts the underlying pickers so they re-display their committed value. The
  // DatePicker clears its own text on an empty edit, so a plain re-render won't restore it.
  const [restoreKey, setRestoreKey] = useState(0);
  function restoreFields() {
    setRestoreKey((key) => key + 1);
  }

  function handleStartDateChange(startDatePickerValue: IDatePickerValue) {
    if (startDatePickerValue.value) {
      const { newStartDate, newEndDate } = getAdjustedStartDateChange(startDatePickerValue.value, endTime);
      onChange({ startTime: newStartDate, endTime: newEndDate });
      onInvalidInput?.('start', false);
      return;
    }

    const isEmpty = startDatePickerValue.inputValue.trim() === '';

    // Non-empty but unparseable: report it as invalid without committing null, so the picker
    // keeps the user's text on screen instead of clearing it.
    if (!isEmpty && onInvalidInput) {
      onInvalidInput('start', true);
      return;
    }

    // Empty input where empty isn't allowed: restore the committed value rather than clearing.
    if (isEmpty && revertEmptyInput && startTime) {
      restoreFields();
      onInvalidInput?.('start', false);
      return;
    }

    onChange({ endTime, startTime: null });
    onInvalidInput?.('start', false);
  }

  function handleEndDateChange(endDatePickerValue: IDatePickerValue) {
    if (endDatePickerValue.value) {
      const { newStartDate, newEndDate } = getAdjustedEndDateChange(endDatePickerValue.value, startTime);
      onChange({ startTime: newStartDate, endTime: newEndDate });
      onInvalidInput?.('end', false);
      return;
    }

    const isEmpty = endDatePickerValue.inputValue.trim() === '';

    if (!isEmpty && onInvalidInput) {
      onInvalidInput('end', true);
      return;
    }

    if (isEmpty && revertEmptyInput && endTime) {
      restoreFields();
      onInvalidInput?.('end', false);
      return;
    }

    onChange({ startTime, endTime: null });
    onInvalidInput?.('end', false);
  }

  return (
    <DateRange
      key={restoreKey}
      className={classNames('adjusting-date-range', className)}
      startDate={startTime}
      endDate={endTime}
      onStartChange={handleStartDateChange}
      onEndChange={handleEndDateChange}
      {...rest}
    />
  );
}

export default AdjustingDateRange;
